// src/components/QuestionsTable.jsx
import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import Swal from "sweetalert2";
import DataTable from "react-data-table-component";
import { useSelector, useDispatch } from "react-redux";
import { selectAdminToken } from "../../sessionSlice";
import { getAdminQustions } from "../../functions/getAdminQuestions";
import "./QuestionsTable.css";
import AddQuestionModal from "../addQuestion/addQuestion";
import QuizSettings from "../quizSettings/quizSettings";
import CsvUploadModal from "../uploadCSV/uploadCSV";
import { getQuestionsCSV } from "../../functions/getQuestionsCSV";
import { deleteQuestionById } from "../../functions/deleteQuestion";
import { useNavigate } from "react-router-dom";
import { deleteAllQuestions } from "../../functions/deleteQuestions";
import { fetchThemeData } from "../../themeSlice";
import { updateThemeData } from "../../functions/updateThemeData";

const QuestionsTable = () => {
  const dispatch = useDispatch();
  const token = useSelector(selectAdminToken);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSettingModalOpen, setIsSettingModalOpen] = useState(false);
  const [isCSVModalOpen, setIsCSVModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const {
    currentTheme,
    user,
    data: themeData,
  } = useSelector((state) => state.theme);
  console.log("check user", user);
  const [rows, setRows] = useState([]);
  const [filterText, setFilterText] = useState("");
  const [resetPaginationToggle, setResetPaginationToggle] = useState(false);
  const [isAIModalOpen, setIsAIModalOpen] = useState(false);
  const [noOfQuestionDraft, setNoOfQuestionDraft] = useState("10");
  const [noOfQuestionSaving, setNoOfQuestionSaving] = useState(false);
  const questionCountWarningShown = useRef(false);

  const navigate = useNavigate();

  const showQuestionCountWarning = useCallback((available, requested) => {
    Swal.fire(
      "Warning",
      `You have ${available} question(s) but want to display ${requested}. Add more questions or lower "No. of questions".`,
      "info"
    );
  }, []);

  // const saveAndContineu = useCallback(() => {
  //   const parsed = Number.parseInt(String(noOfQuestionDraft).trim(), 10);
  //   const requested = Number.isFinite(parsed)
  //     ? Math.min(999, Math.max(1, parsed))
  //     : 1;
  //   const totalAvailableQuestions = rows.length;

  //   if (totalAvailableQuestions < requested) {
  //     showQuestionCountWarning(totalAvailableQuestions, requested);
  //     return;
  //   }

  //   navigate("/admin/questions");
  // }, [navigate, noOfQuestionDraft, rows.length, showQuestionCountWarning]);

  function fixEncoding(value) {
    return value;
  }

  useEffect(() => {
    if (!currentTheme || !token) return;
    questionCountWarningShown.current = false;
    dispatch(fetchThemeData({ themeId: currentTheme }));
  }, [dispatch, currentTheme, token]);

  useEffect(() => {
    if (!themeData || typeof themeData !== "object") return;
    const n = Number(themeData.noOfQuestion);
    const normalized =
      Number.isFinite(n) && n >= 1 ? Math.floor(n) : 10;
    setNoOfQuestionDraft(String(normalized));
  }, [themeData]);

  const handleUpdateNoOfQuestion = useCallback(async () => {
    if (!currentTheme || !token) {
      Swal.fire("Error", "Missing theme or session.", "error");
      return;
    }
    if (!themeData || typeof themeData !== "object") {
      Swal.fire("Error", "Theme data not loaded yet.", "error");
      return;
    }

    const parsed = Number.parseInt(String(noOfQuestionDraft).trim(), 10);
    const next = Number.isFinite(parsed)
      ? Math.min(999, Math.max(1, parsed))
      : 1;
    const totalAvailableQuestions = rows.length;

    if (totalAvailableQuestions < next) {
      showQuestionCountWarning(totalAvailableQuestions, next);
      return;
    }

    setNoOfQuestionSaving(true);
    try {
      await updateThemeData({
        token,
        payload: {
          currentTheme,
          data: {
            ...themeData,
            noOfQuestion: next,
          },
        },
      });
      await dispatch(fetchThemeData({ themeId: currentTheme })).unwrap();
      setNoOfQuestionDraft(String(next));
      Swal.fire("Success", "Number of questions updated.", "success");
    } catch (e) {
      Swal.fire(
        "Error",
        e?.message || String(e) || "Failed to update number of questions.",
        "error"
      );
    } finally {
      setNoOfQuestionSaving(false);
    }
  }, [
    currentTheme,
    dispatch,
    noOfQuestionDraft,
    rows.length,
    showQuestionCountWarning,
    themeData,
    token,
  ]);

  useEffect(() => {
    async function fetchData() {
      if (themeData == null) return true;
      try {
        const response = await getAdminQustions({ currentTheme, token });
        console.log(response);
        const questions = response.questions || [];
        // const formatted = questions.map((q, i) => ({
        //   id: q.id, // Added ID for better row management
        //   no: i + 1,
        //   question: q.question_name,
        //   hint: q.question_hint || '-',
        //   correctAnswer:
        //     q.answer_id === 1 ? q.option_one :
        //     q.answer_id === 2 ? q.option_two :
        //     q.answer_id === 3 ? q.option_three :
        //     q.answer_id === 4 ? q.option_four : '',
        // }));
        const formatted = questions.map((q, i) => {
          const opt1 = fixEncoding(q.option_one || "");
          const opt2 = fixEncoding(q.option_two || "");
          const opt3 = fixEncoding(q.option_three || "");

          return {
            id: q.id,
            no: i + 1,
            question: fixEncoding(q.question_name || ""),
            // build correctAnswer from answer_id using fixed option strings
            correctAnswer:
              q.answer_id === 1
                ? opt1
                : q.answer_id === 2
                ? opt2
                : q.answer_id === 3
                ? opt3
                : "",
            // keep original fields if you need them later (popup, task, images)
            raw: q, // optional: keep raw object for debugging / modals
          };
        });
        console.log(fixEncoding("\u00C2\u0092Hello\u00C2\u0092"));
        console.log("formatted",formatted);
        setRows(formatted);
        const configuredCount = Number(themeData.noOfQuestion);
        if (
          Number.isFinite(configuredCount) &&
          formatted.length < configuredCount &&
          !questionCountWarningShown.current
        ) {
          questionCountWarningShown.current = true;
          showQuestionCountWarning(formatted.length, configuredCount);
        }
      } catch (error) {
        console.error("Error fetching questions:", error);
      }
    }

    if (currentTheme && token) fetchData();
  }, [currentTheme, showQuestionCountWarning, token, themeData]);

  useEffect(() => {
    console.log(rows.length);
  }, []);

  const filteredItems = rows.filter(
    (item) =>
      (item.question &&
        item.question.toLowerCase().includes(filterText.toLowerCase())) ||
      (item.correctAnswer &&
        item.correctAnswer.toLowerCase().includes(filterText.toLowerCase()))
  );

  const subHeaderComponentMemo = useMemo(() => {
    const handleClear = () => {
      if (filterText) {
        setResetPaginationToggle(!resetPaginationToggle);
        setFilterText("");
      }
    };

    return (
      <div className="search-container">
        <input
          type="text"
          placeholder="Search questions or answers"
          value={filterText}
          onChange={(e) => setFilterText(e.target.value)}
          className="search-input"
        />
        <button
          type="button"
          onClick={handleClear}
          className="clear-search-btn"
        >
          Clear
        </button>
      </div>
    );
  }, [filterText, resetPaginationToggle]);

  const columns = [
    {
      name: "",
      selector: (row) => row.no,
      sortable: true,
      width: "80px",
      style: { justifyContent: "center", textAlign: "center" },
    },
    {
      name: "Question",
      selector: (row) => row.question,
      sortable: true,
      wrap: true,
      style: { minWidth: "250px" },
    },
    {
      name: "Correct Answer",
      selector: (row) => row.correctAnswer,
      sortable: true,
      style: { minWidth: "150px" },
    },
{
  name: "Actions",
  cell: (row) => (
    <div className="action-buttons">
      <button className="delete-btn" onClick={() => deleteQuestion(row.id)}>
        <i className="fas fa-trash-alt"></i> Delete
      </button>
      <button className="edit-btn" onClick={() => onEditClick(row.id)}>
        <i className="fas fa-edit"></i> Edit
      </button>
    </div>
  ),
  ignoreRowClick: true,
  width: "200px",
  fixed: "right",        // <- THIS makes it sticky natively
}
  ];

  async function deleteQuestion(id) {
    try {
      const confirmResult = await Swal.fire({
        title: "Are you sure?",
        text: "This will permanently delete this question.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        cancelButtonColor: "#3085d6",
        confirmButtonText: "Yes, delete",
        cancelButtonText: "Cancel",
      });

      if (!confirmResult.isConfirmed) return;

      const response = await deleteQuestionById({ id, token });
      console.log(response);
      if (response.success) {
        window.location.reload();
      }
    } catch (error) {
      console.error("Error fetching questions:", error);
    }
  }

  const onAddClick = () => {
    setEditingId(null); // clear any edit
    setIsModalOpen(true);
  };

  const onEditClick = (qid) => {
    setEditingId(qid); // tell modal what to load
    setIsModalOpen(true);
  };

  const handleExport = async () => {
    try {
      // fetch the CSV as a Blob
      const csvBlob = await getQuestionsCSV({ currentTheme, token });

      // create a URL and trigger download
      const url = window.URL.createObjectURL(csvBlob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "question_block_belong.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
      Swal.fire("Error", err.message || "Could not download CSV", "error");
    }
  };

  async function deleteAllQuestionsTheme() {
    try {
      const response = await deleteAllQuestions({ currentTheme, token });
      console.log(response);
      if (response.success) {
        window.location.reload();
      }
    } catch (error) {
      console.error("Error fetching questions:", error);
    }
  }

  const deleteAll = () => {
    Swal.fire({
      title: "Are you sure?",
      text: "This will permanently delete all questions for this theme.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete all",
      cancelButtonText: "Cancel",
    }).then((result) => {
      if (result.isConfirmed) {
        deleteAllQuestionsTheme();
      }
    });
  };

  const downloadSample = () => {
    const sampleCsv =
      "question_name,option_one,option_two,option_three,correct_answer,answer_id\n" +
      "From where does Veeru propose to Basanti in Sholay?,Top of a roof,Top of a ladder,Top of water tank,Top of water tank,3\n";
    const blob = new Blob([sampleCsv], { type: "text/csv;charset=utf-8;" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "sample-csv.csv";
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  };

  return (
    <>
      <AddQuestionModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        questionId={editingId}
      />

      <QuizSettings
        isOpen={isSettingModalOpen}
        onClose={() => setIsSettingModalOpen(false)}
      />

      <CsvUploadModal
        isOpen={isCSVModalOpen}
        onClose={() => setIsCSVModalOpen(false)}
      />
      <div className="back-button-holder">
        <button
          className="back-button"
          onClick={() => navigate("/admin/rules")}
        >
          <i className="fa-solid fa-arrow-left"></i> Back
        </button>
      </div>
      <div className="table-header">
        
      </div>

      <div className="question-page-blue-holder">
        <div className="blue-tabs-container">
          <div className="blue-tabs"></div>
          <div className="blue-tabs blue-tabs-active"></div>
          <div className="blue-tabs"></div>
        </div>
      </div>

      <div className="questions-table-container">
        <div className="question-header">
          <div className="question-header-title">Questions</div>
        </div>
        <div className="question-header-button question-header-button--top">
          <div className="question-header-left">
            <button
              className="btn outline"
              onClick={() => setIsCSVModalOpen(true)}
            >
              <i className="fa-solid fa-upload"></i> Upload Question CSV
            </button>
            <button className="btn outline" onClick={() => handleExport()}>
              <i className="fa-solid fa-download"></i> Export CSV
            </button>
            <button className="btn outline" onClick={downloadSample}>
              <i className="fa-solid fa-file-csv"></i> Sample CSV
            </button>
          </div>
          <div className="question-header-right">
            <button
              className="add-new-question-v1"
              onClick={() => onAddClick()}
            >
              <i className="fa-solid fa-plus"></i> &nbsp; &nbsp; Add New Question
            </button>
            <button className="btn outline" onClick={() => deleteAll()}>
              <i className="fa-solid fa-trash"></i> Delete All
            </button>
          </div>
        </div>
        <div className="question-header-button question-header-button--bottom">
        <div className="questions-no-of-question-controls">
            <label className="questions-no-of-question-label" htmlFor="admin-no-of-question">
              No. of questions
            </label>
            <input
              id="admin-no-of-question"
              className="questions-no-of-question-input"
              type="number"
              min={1}
              max={999}
              value={noOfQuestionDraft}
              onChange={(e) => setNoOfQuestionDraft(e.target.value)}
              onBlur={() => {
                const n = Number.parseInt(String(noOfQuestionDraft).trim(), 10);
                if (!Number.isFinite(n) || n < 1) setNoOfQuestionDraft("1");
                else if (n > 999) setNoOfQuestionDraft("999");
                else setNoOfQuestionDraft(String(Math.floor(n)));
              }}
              disabled={noOfQuestionSaving || !themeData}
            />
            <button
              type="button"
              className="btn outline questions-no-of-question-save"
              onClick={handleUpdateNoOfQuestion}
              disabled={noOfQuestionSaving || !themeData}
            >
              {noOfQuestionSaving ? "Saving…" : "Update"}
            </button>

            {/* <div className="question-header-action"> */}

          {/* </div> */}
          </div>
        </div>
        <DataTable
          columns={columns}
          data={filteredItems}
          pagination
          paginationResetDefaultPage={resetPaginationToggle}
          subHeader
          subHeaderComponent={subHeaderComponentMemo}
          persistTableHead
          highlightOnHover
          responsive
          noDataComponent={<div className="no-data">No questions found</div>}
        />
        {/* <div className="rules-action-holder">
          <button className="save-and-continue" onClick={saveAndContineu}>
            Save 
          </button>
        </div> */}
      </div>
    </>
  );
};

export default QuestionsTable;